import { useTranslation } from 'react-i18next'
import type { IngestEnvelope } from '@collector/shared'
import { formatPatientLast4, formatTimeHHMM } from '../lib/format'
import {
  asTcccPayload,
  type TcccInjury,
  type TcccMedication,
  type TcccPayload,
  type TcccTourniquet,
  type TcccVitals,
} from '../lib/tccc'
import { VitalsChart } from './VitalsChart'

type TFn = ReturnType<typeof useTranslation>['t']

interface Props {
  envelope: IngestEnvelope
}

/**
 * Renders an ingested `tccc_card@1` data set as a screen-facing facsimile
 * of the DD Form 1380 (Ukrainian edition). Layout flows top-to-bottom:
 *
 *   1. Banner (DD1380 / title / tccc.org.ua)
 *   2. Soldier ID + Evacuation priority row
 *   3. Demographics block
 *   4. Mechanism of Injury checkboxes
 *   5. **Injury Info** — front + back body figures centered, with four
 *      tourniquet cards framing them (RArm/LArm top, RLeg/LLeg bottom)
 *   6. Symptoms & Signs table (plus the full vitals chart)
 *   7. Treatments (C/A/B/C fluids)
 *   8. Medications table (analgesic/antibiotic/other)
 *   9. Other treatments checkbox row
 *  10. Notes
 *  11. Responder
 *
 * Dark mode flips the whole card to white-on-black except the two body
 * figures, which stay on a white panel so their black line-art and green
 * burn-percentage labels remain legible.
 */
export function TcccCardView({ envelope }: Props) {
  const { t } = useTranslation()
  const payload = asTcccPayload(envelope.data)
  const capturedMs = Date.parse(payload.capturedAt ?? envelope.capturedAt)
  const dateStr = Number.isFinite(capturedMs) ? formatFormDate(capturedMs) : '—'
  const timeStr = Number.isFinite(capturedMs) ? formatFormTime(capturedMs) : '—'
  const mechanismTypes = extractMechanismTypes(payload)

  return (
    <div className="space-y-4">
      <article className="overflow-hidden rounded-lg border border-stone-400 bg-white font-sans text-stone-900 shadow-sm dark:border-stone-700 dark:bg-stone-950 dark:text-stone-100">
        {/* --- 1. Banner ------------------------------------------------- */}
        <header className="flex items-baseline justify-between border-b border-stone-400 px-3 py-1.5 text-xs dark:border-stone-600">
          <span className="font-mono font-semibold">DD1380</span>
          <h1 className="text-sm font-bold uppercase tracking-wide">{t('tccc.front.heading')}</h1>
          <span className="font-mono text-stone-500 dark:text-stone-400">tccc.org.ua</span>
        </header>

        {/* --- 2. Soldier ID + Evac ------------------------------------- */}
        <Section>
          <FormLine
            label={t('tccc.front.battleRoster')}
            value={payload.demographics?.battleRoster}
          />
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-semibold">{t('tccc.front.evac')}</span>
            <CheckItem
              label={t('tccc.front.urgent')}
              checked={payload.demographics?.evacPriority === 'A'}
            />
            <CheckItem
              label={t('tccc.front.priority')}
              checked={payload.demographics?.evacPriority === 'B'}
            />
            <CheckItem
              label={t('tccc.front.routine')}
              checked={
                payload.demographics?.evacPriority === 'C' ||
                payload.demographics?.evacPriority === 'D'
              }
            />
          </div>
        </Section>

        {/* --- 3. Demographics ------------------------------------------ */}
        <Section>
          <div className="space-y-2 text-sm">
            <div className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-3">
              <div className="md:col-span-2">
                <FormLine label={t('tccc.front.name')} value={envelope.patient.name} />
              </div>
              <FormLine
                label={t('tccc.front.last4')}
                value={formatPatientLast4(envelope.patient.externalId)}
              />
            </div>
            <div className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-3">
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="font-semibold">{t('tccc.front.gender')}</span>
                <CheckItem
                  label={t('tccc.front.male')}
                  checked={payload.demographics?.gender === 'MALE'}
                />
                <CheckItem
                  label={t('tccc.front.female')}
                  checked={payload.demographics?.gender === 'FEMALE'}
                />
              </div>
              <FormLine label={t('tccc.front.date')} value={dateStr} />
              <FormLine label={t('tccc.front.time')} value={timeStr} />
            </div>
            <div className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-3">
              <FormLine label={t('tccc.front.service')} value={undefined} />
              <FormLine label={t('tccc.front.unit')} value={undefined} />
              <FormLine label={t('tccc.front.age')} value={payload.demographics?.age} />
            </div>
            <FormLine
              label={t('tccc.front.allergies')}
              value={
                payload.demographics?.allergies && payload.demographics.allergies.length > 0
                  ? payload.demographics.allergies.join(', ')
                  : t('tccc.front.nkda')
              }
            />
          </div>
        </Section>

        {/* --- 4. Mechanism of Injury ----------------------------------- */}
        <Section>
          <h3 className="text-sm font-semibold">{t('tccc.front.mechanism')}</h3>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {MECHANISM_CHECKBOXES.map(({ key, tokens }) => (
              <CheckItem
                key={key}
                label={t(`tccc.mechanism.${key}`)}
                checked={tokens.some((tok) => mechanismTypes.has(tok))}
              />
            ))}
            {[...mechanismTypes]
              .filter((tok) => !KNOWN_MECHANISM_TOKENS.has(tok))
              .map((tok) => (
                <CheckItem key={tok} label={humanizeEnum(tok)} checked />
              ))}
          </div>
        </Section>

        {/* --- 5. Injury Info: body figures + tourniquet frame ---------- */}
        <Section>
          <h3 className="mb-2 text-sm font-semibold">{t('tccc.front.injury')}</h3>
          <InjuryFigures
            front={payload.injuriesFront}
            back={payload.injuriesBack}
            tourniquets={payload.tourniquets}
          />
        </Section>

        {/* --- 6. Symptoms table (summary) ------------------------------ */}
        <Section>
          <SymptomsTable vitals={payload.vitalSigns} assessment={payload.assessment} />
        </Section>

        {/* --- 7. Treatments (C / A / B / C-fluids) --------------------- */}
        <Section>
          <h3 className="text-sm font-semibold">{t('tccc.back.treatments')}</h3>
          <TreatmentRow
            prefix={t('tccc.back.cTq')}
            boxes={['EXTREMITY', 'JUNCTIONAL', 'TRUNCAL']}
            items={payload.treatments?.tq}
            t={t}
          />
          <TreatmentRow
            prefix={t('tccc.back.dressing')}
            boxes={['HEMOSTATIC', 'PRESSURE', 'GLASS', 'STANDARD']}
            labelKeys={{
              HEMOSTATIC: 'tccc.treatments.hemostatic',
              PRESSURE: 'tccc.treatments.pressure',
              GLASS: 'tccc.treatments.other',
              STANDARD: 'tccc.treatments.other',
            }}
            items={payload.treatments?.dressing}
            t={t}
          />
          <TreatmentRow
            prefix={t('tccc.back.a')}
            boxes={['NPA', 'CRIC', 'ET_TUBE', 'SGA']}
            labelKeys={{
              NPA: 'tccc.treatments.npa',
              CRIC: 'tccc.treatments.cric',
              ET_TUBE: 'tccc.treatments.etTube',
              SGA: 'tccc.treatments.sga',
            }}
            items={payload.treatments?.airway}
            t={t}
          />
          <TreatmentRow
            prefix={t('tccc.back.b')}
            boxes={['OXYGEN_MASK', 'NEEDLE_D', 'CHEST_TUBE', 'CHEST_SEAL']}
            labelKeys={{
              OXYGEN_MASK: 'tccc.treatments.o2',
              NEEDLE_D: 'tccc.treatments.needleD',
              CHEST_TUBE: 'tccc.treatments.chestTube',
              CHEST_SEAL: 'tccc.treatments.chestSeal',
            }}
            items={payload.treatments?.breathing}
            t={t}
          />
          <div className="mt-3">
            <span className="text-sm font-semibold">{t('tccc.back.cHeader')} </span>
            <FluidsTable
              fluids={payload.medications?.ivFluids}
              blood={[
                ...(payload.medications?.bloodFluids ?? []),
                ...(payload.medications?.plasmaFluids ?? []),
              ]}
              t={t}
            />
          </div>
        </Section>

        {/* --- 8. Medications ------------------------------------------- */}
        <Section>
          <h3 className="text-sm font-semibold">{t('tccc.meds.label')}</h3>
          <MedsTable medications={payload.medications} t={t} />
        </Section>

        {/* --- 9. Other ------------------------------------------------- */}
        <Section>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="font-semibold">{t('tccc.back.other')}</span>
            <CheckItem
              label={t('tccc.treatments.combatPillPack')}
              checked={hasTreatment(payload.treatments?.otherTreatments, 'COMBAT_PILL_PACK')}
            />
            <span className="inline-flex items-center gap-1">
              <CheckItem
                label={t('tccc.treatments.eyeShield')}
                checked={hasAnyTreatment(payload.treatments?.otherTreatments, [
                  'EYE_SHIELD_LEFT',
                  'EYE_SHIELD_RIGHT',
                ])}
              />
              <span className="text-xs text-stone-500 dark:text-stone-400">
                (
                <CheckItem
                  label="R"
                  checked={hasTreatment(payload.treatments?.otherTreatments, 'EYE_SHIELD_RIGHT')}
                />{' '}
                <CheckItem
                  label="L"
                  checked={hasTreatment(payload.treatments?.otherTreatments, 'EYE_SHIELD_LEFT')}
                />
                )
              </span>
            </span>
            <CheckItem
              label={t('tccc.treatments.splint')}
              checked={hasAnyTreatment(payload.treatments?.otherTreatments, [
                'SPLINT_ARM',
                'SPLINT_LEG',
                'SLING',
                'NECK_BRACE',
              ])}
            />
            <CheckItem
              label={t('tccc.treatments.hypothermia')}
              checked={hasTreatment(payload.treatments?.otherTreatments, 'THERMAL_BLANKET')}
            />
          </div>
        </Section>

        {/* --- 10. Notes ------------------------------------------------ */}
        <Section>
          <h3 className="text-sm font-semibold">{t('tccc.back.notes')}</h3>
          <p className="mt-1 min-h-[3rem] whitespace-pre-wrap text-sm">
            {payload.notes && payload.notes.length > 0 ? payload.notes : '—'}
          </p>
        </Section>

        {/* --- 11. Responder (no trailing divider) ---------------------- */}
        <Section last>
          <h3 className="text-sm font-semibold">{t('tccc.back.firstResponder')}</h3>
          <div className="mt-1 grid grid-cols-1 gap-x-4 gap-y-1 md:grid-cols-2">
            <FormLine label={t('tccc.front.name')} value={payload.responder?.name} />
            <FormLine label={t('tccc.back.battleRoster')} value={payload.responder?.battleRoster} />
          </div>
        </Section>
      </article>

      {/* Full vitals chart expands the summary table above. */}
      <section className="card">
        <header className="mb-3 flex items-baseline justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
            {t('tccc.signs.heading')}
          </h2>
          {typeof payload.assessment?.pain === 'number' ? (
            <span className="text-xs text-stone-500 dark:text-stone-400">
              {t('tccc.signs.painLabel')}{' '}
              <span className="font-semibold text-stone-900 dark:text-stone-100">
                {payload.assessment.pain}
              </span>{' '}
              · {t('tccc.signs.avpu')}{' '}
              <span className="font-semibold text-stone-900 dark:text-stone-100">
                {payload.assessment.avpu ?? '—'}
              </span>
            </span>
          ) : null}
        </header>
        <VitalsChart series={payload.vitalSigns ?? []} />
      </section>

      <details>
        <summary className="cursor-pointer text-xs font-medium text-stone-500 dark:text-stone-400">
          {t('visualizer.rawPayload')}
        </summary>
        <pre className="mt-2 overflow-x-auto rounded bg-stone-50 p-3 text-xs dark:bg-stone-900 dark:text-stone-100">
          {JSON.stringify(envelope.data, null, 2)}
        </pre>
      </details>
    </div>
  )
}

/* ------------------------------- subcomponents ----------------------------- */

function Section({ children, last }: { children: React.ReactNode; last?: boolean }) {
  // Each DD1380 row sits between horizontal rules. Last row drops the
  // trailing rule so it meets the card border cleanly.
  const border = last ? '' : 'border-b border-stone-300 dark:border-stone-700'
  return <section className={`px-3 py-2 ${border}`}>{children}</section>
}

function FormLine({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="font-semibold">{label}:</span>
      <span className="flex-1 border-b border-dotted border-stone-400 px-1 pb-0.5 dark:border-stone-500">
        {value && value.length > 0 ? (
          value
        ) : (
          <span className="text-stone-300 dark:text-stone-600">—</span>
        )}
      </span>
    </div>
  )
}

function CheckItem({ label, checked }: { label: string; checked: boolean }) {
  return (
    <span className="inline-flex items-center gap-1 text-sm">
      <span
        aria-hidden
        className={`inline-flex h-3 w-3 shrink-0 items-center justify-center border text-[10px] leading-none ${
          checked
            ? 'border-stone-900 bg-stone-900 text-white dark:border-stone-100 dark:bg-stone-100 dark:text-stone-900'
            : 'border-stone-700 bg-white text-transparent dark:border-stone-400 dark:bg-stone-900'
        }`}
      >
        {checked ? '×' : ''}
      </span>
      <span className={checked ? 'font-medium' : ''}>{label}</span>
    </span>
  )
}

/**
 * Injury type metadata matching the Android InjuryType enum. `shortcut` is
 * the label rendered inside the hexagon marker; `size` controls the marker
 * radius (multiplied by a base px value).
 */
const INJURY_META: Record<string, { shortcut: string; size: number; burn?: boolean }> = {
  ARTILLERY: { shortcut: 'A', size: 2 },
  BURN_1ST: { shortcut: '', size: 0, burn: true },
  BURN_2ND: { shortcut: '', size: 0, burn: true },
  BURN_3RD: { shortcut: '', size: 0, burn: true },
  BLUNT: { shortcut: 'BT', size: 2 },
  DRONE: { shortcut: 'D', size: 2 },
  GRENADE: { shortcut: 'G', size: 2 },
  GSW: { shortcut: 'GSW', size: 1.5 },
  IED: { shortcut: 'IED', size: 3 },
  LANDMINE: { shortcut: 'LM', size: 3 },
  MVC: { shortcut: 'MVC', size: 3 },
  RPG: { shortcut: 'RPG', size: 3 },
  TBI: { shortcut: 'TBI', size: 2.5 },
  OTHER: { shortcut: 'O', size: 3 },
}

const BURN_COLORS: Record<string, string> = {
  BURN_1ST: 'rgba(239,68,68,0.6)',
  BURN_2ND: 'rgba(185,28,28,0.6)',
  BURN_3RD: 'rgba(127,29,29,0.6)',
}

/** The Android body images are 365×565; all injury coordinates live in that space. */
const CANVAS_W = 365
const CANVAS_H = 565

function hexagonPoints(cx: number, cy: number, r: number): string {
  const pts: string[] = []
  for (let i = 0; i < 6; i++) {
    const angle = (i * 60 * Math.PI) / 180
    pts.push(`${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`)
  }
  return pts.join(' ')
}

/**
 * Tourniquet line length varies by y-position on the body (wider at thighs,
 * narrower at wrists). Matches the Android sizeMap.
 */
const TQ_LENGTH_MAP: [number, number][] = [
  [450, 55],
  [500, 60],
  [550, 50],
  [600, 45],
  [650, 45],
  [700, 40],
  [800, 35],
]

function tqLineLength(y: number): number {
  const rounded = Math.round(y / 50) * 50
  const entry = TQ_LENGTH_MAP.find(([k]) => k === rounded)
  return entry ? entry[1] : 30
}

/** Which tourniquet types belong to which body view (matches Android). */
const FRONT_TQ_TYPES = new Set(['TOP_LEFT', 'TOP_RIGHT'])

/**
 * Corner positions for TQ labels, keyed by tourniquet type.
 * Matches the Android layout: arms on front, legs on back.
 */
const TQ_CORNERS: Record<string, { position: string; labelKey: string }> = {
  TOP_LEFT: { position: 'top-0 right-0', labelKey: 'tccc.front.tqLabelRArm' },
  TOP_RIGHT: { position: 'top-0 left-0', labelKey: 'tccc.front.tqLabelLArm' },
  BOTTOM_LEFT: { position: 'bottom-0 right-0', labelKey: 'tccc.front.tqLabelRLeg' },
  BOTTOM_RIGHT: { position: 'bottom-0 left-0', labelKey: 'tccc.front.tqLabelLLeg' },
}

/** Render a body image with SVG injury and tourniquet markers overlaid. */
function BodyFigure({
  src,
  injuries,
  tourniquets,
}: {
  src: string
  injuries?: TcccInjury[]
  tourniquets?: TcccTourniquet[]
}) {
  const { t } = useTranslation()
  const burns = injuries?.filter((inj) => INJURY_META[inj.type]?.burn) ?? []
  const markers =
    injuries?.filter((inj) => !INJURY_META[inj.type]?.burn && inj.points.length > 0) ?? []
  const tqs = tourniquets ?? []

  return (
    <div
      className="relative w-56 max-w-[20rem] sm:w-64 md:w-80"
      style={{ aspectRatio: `${CANVAS_W}/${CANVAS_H}` }}
    >
      <img
        src={src}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-contain"
      />
      <svg
        viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
        className="absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        {burns.map((inj, i) => {
          if (inj.points.length < 3) return null
          const d = inj.points.map((p, j) => `${j === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + ' Z'
          return <path key={i} d={d} fill={BURN_COLORS[inj.type] ?? 'rgba(239,68,68,0.6)'} />
        })}
        {markers.map((inj, i) => {
          const meta = INJURY_META[inj.type] ?? { shortcut: '?', size: 2 }
          const r = meta.size * 10
          const pt = inj.points[0]!
          return (
            <g key={i}>
              <polygon points={hexagonPoints(pt.x, pt.y, r)} fill="#ef4444" />
              <text
                x={pt.x}
                y={pt.y}
                textAnchor="middle"
                dominantBaseline="central"
                fill="white"
                fontWeight="bold"
                fontSize={meta.size <= 1.5 ? 12 : 16}
              >
                {meta.shortcut}
              </text>
            </g>
          )
        })}
        {tqs.map((tq, i) => (
          <line
            key={i}
            x1={tq.x}
            y1={tq.y}
            x2={tq.x + tqLineLength(tq.y)}
            y2={tq.y}
            stroke={tq.active ? '#dc2626' : '#9ca3af'}
            strokeWidth={6}
            strokeLinecap="round"
          />
        ))}
      </svg>
      {tqs.map((tq, i) => {
        const corner = TQ_CORNERS[tq.type]
        if (!corner) return null
        return (
          <span
            key={i}
            className={`absolute ${corner.position} m-1 rounded bg-green-800 px-1.5 py-0.5 text-[0.6rem] font-semibold leading-tight text-white`}
          >
            {t(corner.labelKey)}
          </span>
        )
      })}
    </div>
  )
}

function InjuryFigures({
  front,
  back,
  tourniquets,
}: {
  front?: TcccInjury[]
  back?: TcccInjury[]
  tourniquets?: TcccTourniquet[]
}) {
  const frontTqs = tourniquets?.filter((tq) => FRONT_TQ_TYPES.has(tq.type))
  const backTqs = tourniquets?.filter((tq) => !FRONT_TQ_TYPES.has(tq.type))
  return (
    <div className="flex justify-center gap-2 rounded bg-white p-2 dark:bg-black dark:ring-1 dark:ring-stone-700">
      <BodyFigure src="/tccc-body-front.png" injuries={front} tourniquets={frontTqs} />
      <BodyFigure src="/tccc-body-back.png" injuries={back} tourniquets={backTqs} />
    </div>
  )
}

/**
 * Summary symptoms table — up to four time-stamped column entries across
 * the pulse / BP / RR / SpO₂ / AVPU / pain rows. Echoes the DD1380 page-1
 * table; the full time series appears in the VitalsChart below.
 */
function SymptomsTable({
  vitals,
  assessment,
}: {
  vitals?: TcccVitals[]
  assessment?: TcccPayload['assessment']
}) {
  const { t } = useTranslation()
  const sorted = [...(vitals ?? [])].sort((a, b) => a.time.localeCompare(b.time))
  const columns = pickColumns(sorted, 4)
  // Pad to a consistent width so the table never looks half-filled.
  const slots = [...columns, ...new Array<null>(Math.max(0, 4 - columns.length)).fill(null)]
  const fmt = (v: number | undefined): string =>
    typeof v === 'number' && Number.isFinite(v) ? String(v) : ''
  const sbpDbp = (row: TcccVitals | null): string => {
    if (!row) return ''
    const s = fmt(row.systolicBP)
    const d = fmt(row.diastolicBP)
    if (!s && !d) return ''
    return `${s || '—'} / ${d || '—'}`
  }
  const border = 'border border-stone-300 dark:border-stone-700'
  return (
    <div className="text-sm">
      <h3 className="mb-2 font-semibold">{t('tccc.symptoms.heading')}</h3>
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            <th className={`w-40 text-left font-semibold ${border} px-2 py-1`}>
              {t('tccc.symptoms.time')}
            </th>
            {slots.map((c, i) => (
              <th key={i} className={`px-2 py-1 ${border} bg-stone-50 dark:bg-stone-900`}>
                {c ? formatFormTime(Date.parse(c.time)) : '\u00a0'}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <SymptomRow label={t('tccc.symptoms.pulse')} cells={slots.map((c) => fmt(c?.pulse))} />
          <SymptomRow label={t('tccc.symptoms.bp')} cells={slots.map((c) => sbpDbp(c))} />
          <SymptomRow
            label={t('tccc.symptoms.respiration')}
            cells={slots.map((c) => fmt(c?.respirationRate))}
          />
          <SymptomRow
            label={t('tccc.symptoms.spo2')}
            cells={slots.map((c) => fmt(c?.oxygenSaturation))}
          />
          <SymptomRow
            label={t('tccc.symptoms.avpu')}
            cells={slots.map(() => assessment?.avpu ?? '')}
            accent
          />
          <SymptomRow
            label={t('tccc.symptoms.pain')}
            cells={slots.map(() =>
              typeof assessment?.pain === 'number' ? String(assessment.pain) : '',
            )}
            accent
          />
        </tbody>
      </table>
    </div>
  )
}

function SymptomRow({
  label,
  cells,
  accent,
}: {
  label: string
  cells: string[]
  accent?: boolean
}) {
  const border = 'border border-stone-300 dark:border-stone-700'
  const rowBg = accent ? 'bg-stone-50 dark:bg-stone-900' : ''
  return (
    <tr className={rowBg}>
      <th scope="row" className={`text-left font-semibold ${border} px-2 py-1`}>
        {label}
      </th>
      {cells.map((v, i) => (
        <td key={i} className={`${border} px-2 py-1 text-center`}>
          {v || '\u00a0'}
        </td>
      ))}
    </tr>
  )
}

/**
 * Pick up to `max` evenly-spaced rows from a sorted series so a long
 * vitals timeline still fits in the four-column DD1380 table. If the
 * series is shorter than `max` it's returned as-is.
 */
function pickColumns(sorted: TcccVitals[], max: number): TcccVitals[] {
  if (sorted.length <= max) return sorted
  const step = (sorted.length - 1) / (max - 1)
  const picks: TcccVitals[] = []
  for (let i = 0; i < max; i++) {
    const idx = Math.round(i * step)
    picks.push(sorted[idx]!)
  }
  return picks
}

function TreatmentRow({
  prefix,
  boxes,
  labelKeys,
  items,
  t,
}: {
  prefix: string
  boxes: string[]
  labelKeys?: Record<string, string>
  items?: string[]
  t: TFn
}) {
  const set = new Set(items ?? [])
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
      <span className="font-semibold">{prefix}</span>
      {boxes.map((box) => {
        const key = labelKeys?.[box] ?? defaultTreatmentKey(box)
        const label = key ? t(key) : humanizeEnum(box)
        return <CheckItem key={box} label={label} checked={set.has(box)} />
      })}
    </div>
  )
}

function defaultTreatmentKey(box: string): string | null {
  switch (box) {
    case 'EXTREMITY':
      return 'tccc.treatments.extremity'
    case 'JUNCTIONAL':
      return 'tccc.treatments.junctional'
    case 'TRUNCAL':
      return 'tccc.treatments.truncal'
    case 'INTACT':
      return 'tccc.treatments.intact'
    default:
      return null
  }
}

function FluidsTable({
  fluids,
  blood,
  t,
}: {
  fluids?: TcccMedication[]
  blood?: TcccMedication[]
  t: TFn
}) {
  return (
    <table className="mt-1 w-full border-collapse text-xs">
      <thead>
        <tr className="bg-stone-100 text-left dark:bg-stone-800">
          <th className="w-24 border border-stone-300 px-2 py-1 dark:border-stone-700"></th>
          <th className="border border-stone-300 px-2 py-1 dark:border-stone-700">
            {t('tccc.fluids.colName')}
          </th>
          <th className="border border-stone-300 px-2 py-1 dark:border-stone-700">
            {t('tccc.fluids.colVolume')}
          </th>
          <th className="border border-stone-300 px-2 py-1 dark:border-stone-700">
            {t('tccc.fluids.colRoute')}
          </th>
          <th className="border border-stone-300 px-2 py-1 dark:border-stone-700">
            {t('tccc.fluids.colTime')}
          </th>
        </tr>
      </thead>
      <tbody>
        <FluidRows label={t('tccc.fluids.fluid')} rows={fluids} />
        <FluidRows label={t('tccc.fluids.bloodProduct')} rows={blood} />
      </tbody>
    </table>
  )
}

function FluidRows({ label, rows }: { label: string; rows?: TcccMedication[] }) {
  const list = rows && rows.length > 0 ? rows : [null]
  return (
    <>
      {list.map((m, i) => (
        <tr key={`${label}-${i}`}>
          {i === 0 ? (
            <th
              rowSpan={list.length}
              scope="row"
              className="border border-stone-300 bg-stone-50 px-2 py-1 text-left font-semibold dark:border-stone-700 dark:bg-stone-900"
            >
              {label}
            </th>
          ) : null}
          <td className="border border-stone-300 px-2 py-1 dark:border-stone-700">
            {m ? humanizeEnum(m.name) : '\u00a0'}
          </td>
          <td className="border border-stone-300 px-2 py-1 dark:border-stone-700">
            {m?.dosage ?? ''}
          </td>
          <td className="border border-stone-300 px-2 py-1 dark:border-stone-700">
            {m ? m.form : ''}
          </td>
          <td className="border border-stone-300 px-2 py-1 dark:border-stone-700">
            {m ? formatFormTime(Date.parse(m.time)) : ''}
          </td>
        </tr>
      ))}
    </>
  )
}

function MedsTable({ medications, t }: { medications?: TcccPayload['medications']; t: TFn }) {
  return (
    <table className="mt-1 w-full border-collapse text-xs">
      <thead>
        <tr className="bg-stone-100 text-left dark:bg-stone-800">
          <th className="w-28 border border-stone-300 px-2 py-1 dark:border-stone-700"></th>
          <th className="border border-stone-300 px-2 py-1 dark:border-stone-700">
            {t('tccc.meds.name')}
          </th>
          <th className="border border-stone-300 px-2 py-1 dark:border-stone-700">
            {t('tccc.meds.dose')}
          </th>
          <th className="border border-stone-300 px-2 py-1 dark:border-stone-700">
            {t('tccc.meds.route')}
          </th>
          <th className="border border-stone-300 px-2 py-1 dark:border-stone-700">
            {t('tccc.meds.time')}
          </th>
        </tr>
      </thead>
      <tbody>
        <MedRows label={t('tccc.meds.analgesic')} rows={medications?.analgesics} />
        <MedRows label={t('tccc.meds.antibiotic')} rows={medications?.antibiotics} />
        <MedRows label={t('tccc.meds.other')} rows={medications?.txaFluids} />
      </tbody>
    </table>
  )
}

function MedRows({ label, rows }: { label: string; rows?: TcccMedication[] }) {
  const list = rows && rows.length > 0 ? rows : [null]
  return (
    <>
      {list.map((m, i) => (
        <tr key={`${label}-${i}`}>
          {i === 0 ? (
            <th
              rowSpan={list.length}
              scope="row"
              className="border border-stone-300 bg-stone-50 px-2 py-1 text-left align-top font-semibold dark:border-stone-700 dark:bg-stone-900"
            >
              {label}
            </th>
          ) : null}
          <td className="border border-stone-300 px-2 py-1 dark:border-stone-700">
            {m ? humanizeEnum(m.name) : '\u00a0'}
          </td>
          <td className="border border-stone-300 px-2 py-1 dark:border-stone-700">
            {m?.dosage ?? ''}
          </td>
          <td className="border border-stone-300 px-2 py-1 dark:border-stone-700">
            {m ? m.form : ''}
          </td>
          <td className="border border-stone-300 px-2 py-1 dark:border-stone-700">
            {m ? formatFormTime(Date.parse(m.time)) : ''}
          </td>
        </tr>
      ))}
    </>
  )
}

/* -------------------------------- helpers ---------------------------------- */

const MECHANISM_CHECKBOXES: { key: string; tokens: string[] }[] = [
  { key: 'artillery', tokens: ['ARTILLERY'] },
  { key: 'blunt', tokens: ['BLUNT'] },
  { key: 'burn', tokens: ['BURN_1ST', 'BURN_2ND', 'BURN_3RD'] },
  { key: 'fall', tokens: ['FALL'] },
  { key: 'grenade', tokens: ['GRENADE'] },
  { key: 'gsw', tokens: ['GSW'] },
  { key: 'ied', tokens: ['IED'] },
  { key: 'landmine', tokens: ['LANDMINE'] },
  { key: 'mvc', tokens: ['MVC'] },
  { key: 'rpg', tokens: ['RPG'] },
  { key: 'other', tokens: ['OTHER'] },
]

const KNOWN_MECHANISM_TOKENS = new Set<string>(MECHANISM_CHECKBOXES.flatMap((m) => m.tokens))

function extractMechanismTypes(payload: TcccPayload): Set<string> {
  const set = new Set<string>()
  for (const inj of payload.injuriesFront ?? []) set.add(inj.type)
  for (const inj of payload.injuriesBack ?? []) set.add(inj.type)
  return set
}

function hasTreatment(items: string[] | undefined, token: string): boolean {
  return items?.includes(token) ?? false
}

function hasAnyTreatment(items: string[] | undefined, tokens: string[]): boolean {
  if (!items) return false
  return tokens.some((t) => items.includes(t))
}

function formatFormDate(ms: number): string {
  // DD-MMM-YY per the form's own label. Locale pinned to uk-UA so the month
  // abbreviation is stable regardless of the operator's browser language.
  const d = new Date(ms)
  const day = String(d.getDate()).padStart(2, '0')
  const mon = d.toLocaleString('uk-UA', { month: 'short' }).toUpperCase()
  const yr = String(d.getFullYear()).slice(-2)
  return `${day}-${mon}-${yr}`
}

function formatFormTime(ms: number): string {
  if (!Number.isFinite(ms)) return '—'
  return formatTimeHHMM(new Date(ms))
}

function humanizeEnum(raw: string): string {
  return raw
    .toLowerCase()
    .split('_')
    .map((part) => (part.length > 0 ? part[0]!.toUpperCase() + part.slice(1) : part))
    .join(' ')
}
